import { Avatar, Button, Chip, Icons, Input } from "@jod/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { customerBackendApi, type UpdateProfileRequest } from "../features/api/backend.ts";
import { useT } from "../features/i18n/index.ts";
import { useLiff } from "../features/liff/use-liff.ts";
import { useSession } from "../features/session/session-context.tsx";

export const Route = createFileRoute("/profile")({
  component: ProfileScreen,
});

/** Shared TanStack Query cache key so /profile and /line-profile agree on one row. */
export const PROFILE_QUERY_KEY = ["customer", "profile"] as const;

/** Seed gallery tiles so the mock shows a populated profile out of the box. */
function seedTile(stop: string) {
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 120'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='${stop}'/><stop offset='1' stop-color='#221b13'/></linearGradient></defs><rect width='120' height='120' fill='url(#g)'/><circle cx='60' cy='48' r='20' fill='#e6d6b8' opacity='0.85'/><path d='M22 116c4-22 18-33 38-33s34 11 38 33z' fill='#e6d6b8' opacity='0.85'/></svg>`,
  )}`;
}

// i18n exception (I18N-012-adjacent): vibe tags are persisted profile VALUES —
// the backend stores the selected strings verbatim (`vibes: string[]`), and
// existing rows hold this Thai copy. Localizing the stored values would corrupt
// saved profiles, so they stay literal in both locales for now.
const VIBES = ["ชิล", "ฟังเพลง", "คุยสนุก", "มากับเพื่อน", "สายชิม", "ปาร์ตี้"];

function ProfileScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { profile } = useLiff();
  const { toast } = useSession();
  const queryClient = useQueryClient();

  // FR-CUS-025: profile fields persist server-side in backend mode. Mock mode
  // keeps the original local-only behaviour (no backend to persist to).
  const backendEnabled = customerBackendApi !== null;
  const profileQuery = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: () =>
      customerBackendApi?.getMyProfile() ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendEnabled,
  });
  const updateMutation = useMutation({
    mutationFn: (body: UpdateProfileRequest) => {
      if (!customerBackendApi) {
        return Promise.reject(new Error("Customer backend API unavailable"));
      }
      return customerBackendApi.updateMyProfile(body);
    },
    onSuccess: (data) => queryClient.setQueryData(PROFILE_QUERY_KEY, data),
  });

  // ASSUMPTION: the gallery stays local-only in both modes — there is no
  // object-storage endpoint for customer-liff photos yet (see line-profile.tsx
  // NOTE; needs a new ADR superseding ADR-0011). Only bio/status/vibes/IG/
  // exposure toggles persist server-side.
  const [photos, setPhotos] = useState<string[]>([seedTile("#4a3b28"), seedTile("#3a4a2e")]);
  const [vibes, setVibes] = useState<string[]>(["ชิล", "ฟังเพลง"]);
  const [bio, setBio] = useState("");
  const [status, setStatus] = useState("");
  const [ig, setIg] = useState("");

  // Tiered exposure — what a non-private viewer sees BEFORE you accept them.
  // Name + avatar are always the minimal public layer; these add to it. The
  // full gallery + socials never show until a contact request is mutually
  // accepted (consent-first, PRIV-020 / ADR-0009) — that tier isn't toggleable.
  const [showFirstPhoto, setShowFirstPhoto] = useState(true);
  const [showVibe, setShowVibe] = useState(true);
  const [showBio, setShowBio] = useState(false);

  // Hydrate the editable fields from the persisted profile once loaded.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!profileQuery.data || hydratedRef.current) return;
    hydratedRef.current = true;
    const data = profileQuery.data;
    setVibes(data.vibes);
    setBio(data.bio ?? "");
    setStatus(data.statusLine ?? "");
    setIg(data.instagramHandle ?? "");
    setShowVibe(data.showVibes);
    setShowBio(data.showBio);
  }, [profileQuery.data]);

  const galleryRef = useRef<HTMLInputElement>(null);

  const addPhoto = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotos((prev) => [...prev, URL.createObjectURL(file)]);
    e.target.value = "";
  };

  const removePhoto = (url: string) => {
    setPhotos((prev) => prev.filter((p) => p !== url));
    if (url.startsWith("blob:")) URL.revokeObjectURL(url);
  };

  const toggleVibe = (v: string) =>
    setVibes((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const save = async () => {
    if (backendEnabled) {
      const current = profileQuery.data;
      try {
        await updateMutation.mutateAsync({
          displayName:
            current?.displayName?.trim() || profile?.displayName?.trim() || t("common.you"),
          avatarType: current?.avatarType ?? "line",
          avatarGlyph: current?.avatarGlyph ?? null,
          statusLine: status.trim() || null,
          bio: bio.trim() || null,
          vibes,
          instagramHandle: ig.trim() || null,
          showVibes: showVibe,
          showBio,
        });
      } catch {
        toast(t("profile.saveFailed"));
        return;
      }
    }
    toast(t("profile.savedToast"));
    navigate({ to: "/me" });
  };

  return (
    <div className="mx-auto w-full max-w-md px-[18px] py-6">
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-cream-3 text-sm"
        onClick={() => navigate({ to: "/me" })}
      >
        <Icons.IconBack size={18} /> {t("common.back")}
      </button>

      <div className="flex items-center gap-4">
        <Avatar
          imageUrl={profile?.pictureUrl}
          label={profile?.displayName ?? t("common.you")}
          glyph={profile?.pictureUrl ? undefined : "🌙"}
          size={64}
          ring
        />
        <div className="min-w-0">
          <div className="jod-kicker">{t("profile.kicker")}</div>
          <h1 className="h-display truncate text-[1.5rem]">
            {profile?.displayName ?? t("common.you")}
          </h1>
        </div>
      </div>

      {/* Gallery */}
      <Section title={t("profile.galleryTitle")} hint={t("profile.galleryHint")}>
        <div className="grid grid-cols-3 gap-2">
          {photos.map((url) => (
            <div key={url} className="relative aspect-square overflow-hidden rounded-lg">
              <img src={url} alt="" className="size-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(url)}
                aria-label={t("profile.removePhotoAria")}
                className="absolute top-1 right-1 grid size-6 place-items-center rounded-full bg-[oklch(13%_0.009_58_/_75%)] text-cream"
              >
                <Icons.IconClose size={13} />
              </button>
            </div>
          ))}
          {photos.length < 6 ? (
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              className="grid aspect-square place-items-center rounded-lg border border-hairline border-dashed text-cream-3"
            >
              <Icons.IconPlus size={22} />
            </button>
          ) : null}
        </div>
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={addPhoto}
        />
      </Section>

      {/* Bio + status */}
      <Section title={t("profile.introTitle")}>
        <input
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          placeholder={t("profile.statusPlaceholder")}
          maxLength={60}
          className="w-full rounded-lg border border-hairline bg-[oklch(100%_0_0_/_3%)] px-3.5 py-3 text-cream text-sm outline-none placeholder:text-cream-4 focus:border-gold"
        />
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder={t("profile.bioPlaceholder")}
          rows={3}
          maxLength={240}
          className="mt-2 w-full resize-none rounded-lg border border-hairline bg-[oklch(100%_0_0_/_3%)] px-3.5 py-3 text-cream text-sm leading-relaxed outline-none placeholder:text-cream-4 focus:border-gold"
        />
      </Section>

      {/* Vibe tags */}
      <Section title={t("profile.vibeTitle")}>
        <div className="flex flex-wrap gap-2">
          {VIBES.map((v) => (
            <Chip key={v} on={vibes.includes(v)} onClick={() => toggleVibe(v)}>
              {v}
            </Chip>
          ))}
        </div>
      </Section>

      {/* Socials — released only after mutual accept */}
      <Section title={t("profile.socialTitle")} hint={t("profile.socialHint")}>
        <div className="flex items-center gap-2 rounded-lg border border-hairline bg-[oklch(100%_0_0_/_3%)] px-3.5 py-3">
          <Icons.IconIG size={18} className="text-cream-3" />
          <Input
            value={ig}
            onChange={(e) => setIg(e.target.value)}
            placeholder={t("profile.igPlaceholder")}
            className="border-0 bg-transparent px-0"
          />
        </div>
      </Section>

      {/* Tiered exposure control */}
      <Section title={t("profile.exposureTitle")} hint={t("profile.exposureHint")}>
        <div className="glass space-y-1 rounded-xl p-1.5">
          <Tier
            tone="muted"
            icon={<Icons.IconLock size={16} />}
            label={t("profile.tierHidden")}
            value={t("profile.tierHiddenValue")}
          />
        </div>

        <div className="mt-2 space-y-2.5">
          <p className="jod-kicker">{t("profile.whenOpen")}</p>
          <ExposureToggle
            label={t("profile.exposureBase")}
            sub={t("profile.exposureBaseSub")}
            on
            locked
          />
          <ExposureToggle
            label={t("profile.exposureFirstPhoto")}
            on={showFirstPhoto}
            onToggle={() => setShowFirstPhoto((v) => !v)}
          />
          <ExposureToggle
            label={t("profile.exposureVibe")}
            on={showVibe}
            onToggle={() => setShowVibe((v) => !v)}
          />
          <ExposureToggle
            label={t("profile.exposureBio")}
            on={showBio}
            onToggle={() => setShowBio((v) => !v)}
          />
        </div>
      </Section>

      <Button size="lg" className="mt-6 w-full" disabled={updateMutation.isPending} onClick={save}>
        {updateMutation.isPending ? t("common.saving") : t("profile.save")}
      </Button>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="font-display font-semibold text-cream">{title}</h2>
      {hint ? (
        <p className="mt-0.5 mb-2.5 text-cream-3 text-xs">{hint}</p>
      ) : (
        <div className="mb-2.5" />
      )}
      {children}
    </section>
  );
}

function Tier({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  tone?: "muted";
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg px-3 py-2.5">
      <span className={tone === "muted" ? "text-cream-4" : "text-gold"}>{icon}</span>
      <span className="flex-1 text-cream-2 text-sm">{label}</span>
      <span className="text-cream-3 text-xs">{value}</span>
    </div>
  );
}

function ExposureToggle({
  label,
  sub,
  on,
  onToggle,
  locked,
}: {
  label: string;
  sub?: string;
  on: boolean;
  onToggle?: () => void;
  locked?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={locked}
      aria-pressed={on}
      className="glass flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left disabled:opacity-90"
    >
      <div className="min-w-0 flex-1">
        <div className="text-cream text-sm">{label}</div>
        {sub ? <div className="text-cream-4 text-xs">{sub}</div> : null}
      </div>
      <span
        className={`flex h-6 w-10 shrink-0 items-center rounded-full p-0.5 transition-colors ${
          on ? "bg-lime" : "bg-[oklch(100%_0_0_/_12%)]"
        }`}
      >
        <span
          className="size-5 rounded-full bg-cream transition-transform"
          style={{ transform: on ? "translateX(1rem)" : "translateX(0)" }}
        />
      </span>
    </button>
  );
}
